import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Search, Plus, Eye, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { superFetch, formatDate } from '@/lib/super-admin-api';
import type { Tenant, PaginatedResponse } from '@/lib/super-admin-types';

export function TenantsPage() {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const fetchTenants = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (statusFilter) params.set('status', statusFilter);
      params.set('page', String(page));
      params.set('limit', '15');

      const res = await superFetch(`/api/super-admin/tenants?${params}`);
      if (res.ok) {
        const data: PaginatedResponse<Tenant> = await res.json();
        setTenants(data.data || []);
        setTotalPages(data.pagination?.totalPages || 1);
        setTotal(data.pagination?.total || 0);
      }
    } catch {
      toast.error('Erro ao carregar estabelecimentos');
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, page]);

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [search, statusFilter]);

  const inputStyle = { background: '#0f172a', border: '1px solid #334155', color: '#e2e8f0' };

  const statusBadge = (status: string) => {
    const styles: Record<string, { bg: string; color: string; label: string }> = {
      ativo: { bg: '#10b98120', color: '#10b981', label: 'Ativo' },
      bloqueado: { bg: '#ef444420', color: '#ef4444', label: 'Bloqueado' },
      inativo: { bg: '#64748b20', color: '#94a3b8', label: 'Inativo' },
    };
    const s = styles[status] || styles.inativo!;
    return (
      <span
        className="rounded-full px-2.5 py-1 text-xs font-medium"
        style={{ background: s.bg, color: s.color }}
      >
        {s.label}
      </span>
    );
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#f1f5f9' }}>Estabelecimentos</h1>
          <p className="text-base mt-1" style={{ color: '#94a3b8' }}>
            {total} cadastrado{total !== 1 ? 's' : ''}
          </p>
        </div>
        <button
          onClick={() => navigate('/super-admin/tenants/new')}
          className="flex items-center gap-2 rounded-lg px-4 py-2.5 text-base font-medium text-white transition-colors hover:opacity-90"
          style={{ background: '#10b981' }}
        >
          <Plus className="h-4 w-4" />
          Novo Estabelecimento
        </button>
      </div>

      {/* Filters */}
      <div className="mb-4 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" style={{ color: '#64748b' }} />
          <input
            type="text"
            placeholder="Buscar por nome ou CNPJ..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-lg py-2.5 pl-10 pr-4 text-base outline-none focus:ring-2 focus:ring-emerald-500/50"
            style={inputStyle}
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-lg px-3 py-2.5 text-base outline-none"
          style={inputStyle}
        >
          <option value="">Todos os status</option>
          <option value="ativo">Ativos</option>
          <option value="bloqueado">Bloqueados</option>
          <option value="inativo">Inativos</option>
        </select>
      </div>

      {/* Table */}
      {loading ? (
        <div className="animate-pulse rounded-xl p-8" style={{ background: '#1e293b' }}>
          <div className="h-64" />
        </div>
      ) : tenants.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 rounded-xl" style={{ background: '#1e293b' }}>
          <Building2 className="h-12 w-12 mb-3" style={{ color: '#475569' }} />
          <p className="text-base" style={{ color: '#94a3b8' }}>Nenhum estabelecimento encontrado</p>
        </div>
      ) : (
        <>
          <div className="rounded-xl border overflow-hidden" style={{ background: '#1e293b', borderColor: '#334155' }}>
            <table className="w-full text-base">
              <thead>
                <tr style={{ borderBottom: '1px solid #334155' }}>
                  <th className="px-5 py-3.5 text-left text-sm font-medium" style={{ color: '#94a3b8' }}>Estabelecimento</th>
                  <th className="px-5 py-3.5 text-left text-sm font-medium" style={{ color: '#94a3b8' }}>CNPJ</th>
                  <th className="px-5 py-3.5 text-left text-sm font-medium" style={{ color: '#94a3b8' }}>Plano</th>
                  <th className="px-5 py-3.5 text-left text-sm font-medium" style={{ color: '#94a3b8' }}>Vencimento</th>
                  <th className="px-5 py-3.5 text-left text-sm font-medium" style={{ color: '#94a3b8' }}>Status</th>
                  <th className="px-5 py-3.5 text-right text-sm font-medium" style={{ color: '#94a3b8' }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {tenants.map((tenant) => (
                  <tr
                    key={tenant.id}
                    className="transition-colors hover:bg-white/5 cursor-pointer"
                    style={{ borderBottom: '1px solid #334155' }}
                    onClick={() => navigate(`/super-admin/tenants/${tenant.id}`)}
                  >
                    <td className="px-5 py-3.5">
                      <p className="font-medium" style={{ color: '#f1f5f9' }}>{tenant.nome_fantasia}</p>
                      <p className="text-sm mt-0.5" style={{ color: '#64748b' }}>{tenant.razao_social}</p>
                    </td>
                    <td className="px-5 py-3.5 font-mono text-sm" style={{ color: '#94a3b8' }}>{tenant.cnpj}</td>
                    <td className="px-5 py-3.5">
                      <span style={{ color: '#f1f5f9' }}>{tenant.plan_name}</span>
                      <span className="ml-1.5 text-sm" style={{ color: '#64748b' }}>
                        R${Number(tenant.plan_price || 0).toFixed(0)}/mês
                      </span>
                    </td>
                    <td className="px-5 py-3.5" style={{ color: '#94a3b8' }}>
                      {tenant.subscription_expires_at ? formatDate(tenant.subscription_expires_at) : '—'}
                    </td>
                    <td className="px-5 py-3.5">{statusBadge(tenant.status)}</td>
                    <td className="px-5 py-3.5 text-right">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/super-admin/tenants/${tenant.id}`);
                        }}
                        className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors hover:opacity-80"
                        style={{ background: '#334155', color: '#e2e8f0' }}
                      >
                        <Eye className="h-4 w-4" />
                        Detalhes
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <p className="text-sm" style={{ color: '#64748b' }}>
                Página {page} de {totalPages}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium transition-colors disabled:opacity-40"
                  style={{ background: '#334155', color: '#e2e8f0' }}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Anterior
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium transition-colors disabled:opacity-40"
                  style={{ background: '#334155', color: '#e2e8f0' }}
                >
                  Próxima
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
